import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TooltipContentProps } from 'recharts';
import Sidebar from '../components/layout/Sidebar';
import { Badge, Button, Card, Icon, SegmentedControl, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';
import HostedSitesEditor from '../components/hosting/HostedSitesEditor';
import HostingRunsCard from '../components/hosting/HostingRunsCard';
import RunButton from '../components/hosting/RunButton';
import { useHostingRuns } from '../components/hosting/useHostingRuns';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import {
  HOSTING_STATUS_LABEL,
  cancelHostingRun,
  fetchHostingAccount,
  fetchHostingBilling,
  fetchHostingChecks,
  fetchMonitoredSites,
  fetchUptimeDaily,
  isReviewedRun,
  isRunnableSite,
  queueHostingRun,
  reviewHostingRun,
  type HostedSite,
  type HostingAccount,
  type HostingBilling,
  type HostingCheck,
  type HostingStatus,
  type PaymentStatus,
  type UptimeDay,
} from '../lib/hosting';

const STATUS_TONE: Record<HostingStatus, BadgeTone> = { active: 'up', paused: 'warn', cancelled: 'neutral' };

const PAYMENT: Record<PaymentStatus, { label: string; tone: BadgeTone; color: string }> = {
  current: { label: 'Current', tone: 'up', color: 'var(--status-up)' },
  past_due: { label: 'Past due', tone: 'down', color: 'var(--status-down)' },
  unpaid: { label: 'Delinquent', tone: 'down', color: 'var(--status-down)' },
  failing: { label: 'Payment failed', tone: 'warn', color: 'var(--status-warn)' },
  canceled: { label: 'Canceled', tone: 'idle', color: 'var(--status-idle)' },
  no_subscription: { label: 'No subscription', tone: 'neutral', color: 'var(--text-muted)' },
  unlinked: { label: 'Not linked', tone: 'neutral', color: 'var(--text-muted)' },
};

type Range = '30' | '90';

const money = (n: number | null | undefined, digits = 2) =>
  n === null || n === undefined
    ? '—'
    : n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
const host = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
const shortDate = (iso: string | null | undefined, withYear = false) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) })
    : '—';
const ago = (iso: string) => {
  // Calendar days, so a check on the 1st reads "4 days ago" on the 5th at any hour.
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(new Date(iso))) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 45) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months} months ago` : `${(days / 365).toFixed(1)} years ago`;
};
const pct = (up: number, checks: number) => (checks ? (up / checks) * 100 : null);
const pctText = (p: number | null) => (p === null ? '—' : `${p >= 99.995 ? '100' : p.toFixed(2)}%`);
const dayColor = (p: number | null) =>
  p === null ? 'var(--surface-active)' : p >= 99.9 ? 'var(--status-up)' : p >= 99 ? 'var(--status-warn)' : 'var(--status-down)';

/** The last N calendar days (Denver), oldest first, as YYYY-MM-DD. */
function dayKeys(n: number): string[] {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Denver' });
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(fmt.format(new Date(Date.now() - i * 86_400_000)));
  return out;
}

export default function HostingDetail() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();

  const [account, setAccount] = useState<HostingAccount | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  // Every monitored site: the Websites card offers the unattached ones.
  const [allSites, setAllSites] = useState<HostedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [range, setRange] = useState<Range>('90');
  const [uptime, setUptime] = useState<UptimeDay[]>([]);
  const [uptimeLoading, setUptimeLoading] = useState(true);

  const [billing, setBilling] = useState<HostingBilling | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [checks, setChecks] = useState<{ last: HostingCheck | null; next: HostingCheck | null } | null>(null);
  const [showAllPayments, setShowAllPayments] = useState(false);

  const { user } = useUser();
  const toast = useToast();
  const [runBusy, setRunBusy] = useState(false);
  // A finished run may have closed this month's check, so re-read it then.
  const reloadChecks = useCallback(() => {
    fetchHostingChecks()
      .then(setChecks)
      .catch((err) => console.error('Error reloading hosting checks:', err));
  }, []);
  const runs = useHostingRuns(accountId, reloadChecks);

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      const [a, allSites, hostingChecks] = await Promise.all([fetchHostingAccount(accountId), fetchMonitoredSites(), fetchHostingChecks()]);
      if (!a) {
        setNotFound(true);
        return;
      }
      setAccount(a);
      setAllSites(allSites);
      setChecks(hostingChecks);
      if (a.client_id) {
        const { data } = await supabase.from('clients').select('name').eq('id', a.client_id).maybeSingle();
        setClientName(data?.name ?? null);
      }
    } catch (err) {
      console.error('Error loading hosting account:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load the hosting account');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  // Attach, detach and auto-linking only change sites; no need to re-read the account.
  const reloadSites = useCallback(async () => {
    try {
      setAllSites(await fetchMonitoredSites());
    } catch (err) {
      console.error('Error reloading hosted sites:', err);
    }
  }, []);
  const sites = useMemo(() => allSites.filter((s) => s.hosting_account_id === accountId), [allSites, accountId]);

  // Stripe is a network hop away; the page renders without waiting for it.
  useEffect(() => {
    if (!accountId) return;
    setBilling(null);
    setBillingError(null);
    fetchHostingBilling(accountId)
      .then(setBilling)
      .catch((err) => setBillingError(err instanceof Error ? err.message : 'Could not reach Stripe'));
  }, [accountId]);

  // Keyed on the ids, so re-reading the sites (the Websites card does while one links) doesn't refetch uptime.
  const siteKey = sites.map((s) => s.id).join(',');
  const siteIds = useMemo(() => (siteKey ? siteKey.split(',') : []), [siteKey]);
  useEffect(() => {
    if (loading) return;
    setUptimeLoading(true);
    fetchUptimeDaily(siteIds, Number(range))
      .then(setUptime)
      .catch((err) => console.error('Error loading uptime:', err))
      .finally(() => setUptimeLoading(false));
  }, [siteIds, range, loading]);

  const days = useMemo(() => dayKeys(Number(range)), [range]);

  const perSite = useMemo(
    () =>
      sites.map((s) => {
        const rows = new Map(uptime.filter((u) => u.site_id === s.id).map((u) => [u.day, u]));
        const total = [...rows.values()].reduce((acc, r) => ({ checks: acc.checks + r.checks, up: acc.up + r.up }), { checks: 0, up: 0 });
        return { site: s, rows, availability: pct(total.up, total.checks) };
      }),
    [sites, uptime]
  );

  const overall = useMemo(() => {
    const t = uptime.reduce((acc, r) => ({ checks: acc.checks + r.checks, up: acc.up + r.up }), { checks: 0, up: 0 });
    return pct(t.up, t.checks);
  }, [uptime]);

  const responseSeries = useMemo(
    () =>
      days.map((d) => {
        const rows = uptime.filter((u) => u.day === d && u.avg_ms !== null);
        const weight = rows.reduce((s, r) => s + r.up, 0);
        const ms = weight ? Math.round(rows.reduce((s, r) => s + (r.avg_ms || 0) * r.up, 0) / weight) : null;
        return { day: d, label: shortDate(`${d}T12:00:00`), ms };
      }),
    [days, uptime]
  );
  const avgMs = useMemo(() => {
    const v = responseSeries.filter((p) => p.ms !== null).map((p) => p.ms as number);
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
  }, [responseSeries]);

  if (notFound || loadError) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--text-muted)' }}>{loadError || 'Hosting account not found'}</p>
          <Link to="/hosting" className="hover-underline" style={{ display: 'inline-block', marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--accent-text)' }}>
            Back to Hosting
          </Link>
        </div>
      </Shell>
    );
  }

  if (loading || !account) return <Shell>{null}</Shell>;

  const included = account.monthly_price === 0 && /retainer/i.test(account.plan || '');
  const pay = billing ? PAYMENT[billing.paymentStatus] : null;
  // The import's "Stripe sub_… (cus_…)." note is what the Stripe button already links.
  const notes = (account.notes || '').replace(/Stripe sub_\w+ \(cus_\w+\)\.\s*/g, '').trim();
  const stripeUrl = account.stripe_customer_id ? `https://dashboard.stripe.com/customers/${account.stripe_customer_id}` : null;
  const runnableCount = sites.filter(isRunnableSite).length;
  // The last check is the newest of the recurring Managed Hosting task and this account's checklist runs:
  // a run counts when it passed, or once someone marked its attention/failure reviewed.
  const lastCheck = [
    checks?.last?.updated_at ?? null,
    ...runs.runs.map((r) => (r.status === 'passed' ? r.finished_at : isReviewedRun(r) ? r.reviewed_at : null)),
  ]
    .filter((d): d is string => Boolean(d))
    .sort()
    .pop();

  const runAction = async (fn: () => Promise<unknown>, done: string) => {
    setRunBusy(true);
    try {
      await fn();
      toast.success(done);
    } catch (err) {
      console.error('Error updating hosting run:', err);
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      // Either way, show what the database now says.
      await runs.refresh();
      setRunBusy(false);
    }
  };

  return (
    <Shell>
      {/* Header */}
      <header
        className="flex flex-col md:flex-row md:items-center justify-between gap-3"
        style={{ padding: 'var(--space-5) 0', borderBottom: '1px solid var(--border-hairline)', marginBottom: 'var(--space-6)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <Link
            to="/hosting"
            className="pressable hover-wash"
            title="Back to Hosting"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--control-sm)',
              height: 'var(--control-sm)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-muted)',
              flexShrink: 0,
            }}
          >
            <Icon name="chevron-left" size={16} />
          </Link>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Wraps on phones rather than cutting the account name short */}
              <h1
                className="md:truncate"
                style={{
                  margin: 0,
                  fontSize: 'var(--text-lg)',
                  fontWeight: 600,
                  letterSpacing: 'var(--tracking-tight)',
                  color: 'var(--text-body)',
                  minWidth: 0,
                }}
              >
                {account.name}
              </h1>
              <Badge tone={STATUS_TONE[account.status]} dot>
                {HOSTING_STATUS_LABEL[account.status]}
              </Badge>
            </div>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {[account.provider, account.plan, account.started_on ? `since ${shortDate(account.started_on + 'T12:00:00', true)}` : null]
                .filter(Boolean)
                .join(' · ') || 'Managed hosting'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
          <RunButton
            disabledReason={runnableCount ? null : 'No Cloudways or Netlify site linked'}
            latest={runs.latest}
            busy={runBusy}
            onRun={(updates) =>
              runAction(() => queueHostingRun(account.id, { updates }, user?.name ?? null), updates ? 'Checklist queued' : 'Check queued (no updates)')
            }
            onCancel={() => runs.latest && runAction(() => cancelHostingRun(runs.latest!.id), 'Run cancelled')}
          />
          {stripeUrl ? (
            <a href={stripeUrl} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="secondary" icon="external-link">
                Stripe
              </Button>
            </a>
          ) : null}
          <Button size="sm" variant="secondary" icon="pencil" onClick={() => navigate(`/hosting?edit=${account.id}`)}>
            Edit
          </Button>
        </div>
      </header>

      {/* Headline figures */}
      <div className="grid grid-cols-2 xl:grid-cols-5" style={{ gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <Stat
          label="Monthly"
          value={included ? 'Included' : money(account.monthly_price)}
          hint={included ? account.plan?.replace(/^Included with /i, 'With ') : account.plan || 'No plan set'}
        />
        <Stat
          label="Lifetime value"
          value={billing?.linked ? money(billing.lifetimeValue, 0) : billing ? '—' : <Skeleton />}
          hint={
            billingError
              ? 'Stripe unavailable'
              : billing?.linked
                ? `${billing.paymentCount} payment${billing.paymentCount === 1 ? '' : 's'}${billing.firstPaymentAt ? ` since ${shortDate(billing.firstPaymentAt, true)}` : ''}`
                : billing
                  ? 'No Stripe customer linked'
                  : 'Asking Stripe…'
          }
        />
        <Stat
          label="Last payment"
          value={billing?.lastPayment ? money(billing.lastPayment.amount) : billing ? '—' : <Skeleton />}
          hint={billing?.lastPayment ? `${shortDate(billing.lastPayment.at, true)} · ${ago(billing.lastPayment.at)}` : billingError ? 'Stripe unavailable' : billing ? 'No payments yet' : ' '}
        />
        <Stat
          label="Payment status"
          value={pay ? <span style={{ color: pay.color }}>{pay.label}</span> : billingError ? '—' : <Skeleton />}
          dotColor={pay?.color}
          hint={billingError ? billingError : billing ? paymentHint(billing) : ' '}
        />
        <Stat
          label="Last hosting check"
          value={lastCheck ? shortDate(lastCheck) : '—'}
          hint={
            lastCheck
              ? `${ago(lastCheck)}${
                  checks?.next?.status === 'In Progress' ? ' · next in progress' : checks?.next?.due_date ? ` · next ${shortDate(checks.next.due_date)}` : ''
                }`
              : checks?.next?.due_date
                ? `First due ${shortDate(checks.next.due_date)}`
                : 'No check yet'
          }
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3" style={{ gap: 'var(--space-5)', alignItems: 'start' }}>
        <div className="xl:col-span-2" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', minWidth: 0 }}>
          <HostingRunsCard
            runs={runs.runs}
            steps={runs.steps}
            loaded={runs.loaded}
            error={runs.error}
            sites={sites}
            runnableCount={runnableCount}
            fixCtx={{
              account,
              clientName: account.client_id ? clientName : null,
              user: user?.id ? { id: user.id, name: user.name || user.email || 'Someone' } : null,
              onError: (m) => toast.error(m),
              onQueued: (m) => toast.success(m),
            }}
            onReview={(run, reviewed, note) =>
              runAction(() => reviewHostingRun(run.id, reviewed, user?.name ?? null, note), reviewed ? 'Marked reviewed' : 'Review taken back')
            }
          />

          {/* Uptime */}
          <Card
            eyebrow={`Last ${range} days`}
            title="Uptime"
            actions={
              <SegmentedControl
                options={[
                  { id: '30', label: '30d' },
                  { id: '90', label: '90d' },
                ]}
                value={range}
                onChange={(id) => setRange(id as Range)}
              />
            }
          >
            {sites.length === 0 ? (
              <EmptyNote>No websites yet. Add one under Websites and its uptime shows here.</EmptyNote>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', opacity: uptimeLoading ? 0.5 : 1, transition: 'opacity 160ms ease' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                  <span className="t-num" style={{ fontSize: 'var(--text-3xl)', fontWeight: 600, letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
                    {pctText(overall)}
                  </span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                    {sites.length > 1 ? `across ${sites.length} sites` : 'uptime'}
                    {avgMs !== null ? ` · ${avgMs}ms average response` : ''}
                  </span>
                </div>

                {perSite.map(({ site, rows, availability }) => (
                  <div key={site.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' }}>
                      <Link to={`/uptime/${site.id}`} className="hover-underline" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {host(site.url)}
                      </Link>
                      <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: dayColor(availability) === 'var(--surface-active)' ? 'var(--text-faint)' : dayColor(availability) }}>
                        {pctText(availability)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: range === '90' ? '2px' : '3px', height: '34px' }}>
                      {days.map((d) => {
                        const r = rows.get(d);
                        const p = r ? pct(r.up, r.checks) : null;
                        return (
                          <div
                            key={d}
                            title={`${shortDate(`${d}T12:00:00`, true)}: ${p === null ? 'no data' : `${pctText(p)} up${r?.avg_ms ? ` · ${r.avg_ms}ms` : ''}`}`}
                            style={{ flex: 1, minWidth: 0, borderRadius: '2px', background: dayColor(p), opacity: p === null ? 1 : 0.9 }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', rowGap: '8px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '-8px' }}>
                  <span>{shortDate(`${days[0]}T12:00:00`)}</span>
                  {/* Phones: the legend drops to its own line under the date ends */}
                  <span className="order-last w-full md:order-none md:w-auto" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
                    {[
                      ['var(--status-up)', '99.9%+'],
                      ['var(--status-warn)', '99%+'],
                      ['var(--status-down)', 'Below 99%'],
                      ['var(--surface-active)', 'No data'],
                    ].map(([c, l]) => (
                      <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: c }} />
                        {l}
                      </span>
                    ))}
                  </span>
                  <span>Today</span>
                </div>

                <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: 'var(--space-4)' }}>
                  <div className="t-eyebrow" style={{ marginBottom: 'var(--space-3)' }}>Response time · daily average</div>
                  <div style={{ height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={responseSeries} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="hosting-rt" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke="var(--border-hairline)" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={28} tick={{ fill: 'var(--text-faint)', fontSize: 11 }} />
                        <YAxis tickLine={false} axisLine={false} width={56} tick={{ fill: 'var(--text-faint)', fontSize: 11 }} tickFormatter={(v) => `${v}ms`} />
                        <Tooltip content={(p: TooltipContentProps<number, string>) => <ChartTip {...p} />} cursor={{ stroke: 'var(--border-strong)' }} />
                        <Area type="monotone" dataKey="ms" stroke="var(--accent)" strokeWidth={2} fill="url(#hosting-rt)" connectNulls dot={false} activeDot={{ r: 4 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Payments */}
          <Card eyebrow="Stripe" title="Payments">
            {billingError ? (
              <EmptyNote tone="down">{billingError}</EmptyNote>
            ) : !billing ? (
              <EmptyNote>Asking Stripe…</EmptyNote>
            ) : !billing.linked ? (
              <EmptyNote>This account is not linked to a Stripe customer yet.</EmptyNote>
            ) : !billing.payments?.length ? (
              <EmptyNote>No payments on this customer yet.</EmptyNote>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {billing.payments.slice(0, showAllPayments ? undefined : 8).map((p, i) => (
                  <div
                    key={p.id}
                    // Phones drop the description column ("Subscrip…" was all that fit)
                    className="grid grid-cols-[auto_1fr_auto] gap-3 md:grid-cols-[110px_1fr_auto_auto] md:gap-[16px]"
                    style={{
                      alignItems: 'center',
                      padding: '11px 0',
                      borderTop: i ? '1px solid var(--border-hairline)' : 'none',
                    }}
                  >
                    <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                      {shortDate(p.at, true)}
                    </span>
                    <span className="hidden md:block" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {describe(p.description)}
                    </span>
                    <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: p.status === 'succeeded' ? 'var(--text-body)' : 'var(--text-faint)', textAlign: 'right' }}>
                      {money(p.amount)}
                      {p.refunded ? <span style={{ color: 'var(--text-faint)' }}> · {money(p.refunded)} refunded</span> : null}
                    </span>
                    <Badge tone={p.status === 'succeeded' ? 'up' : p.status === 'failed' ? 'down' : 'warn'}>
                      {p.status === 'succeeded' ? 'Paid' : p.status === 'failed' ? 'Failed' : 'Pending'}
                    </Badge>
                  </div>
                ))}
                {billing.payments.length > 8 ? (
                  <button
                    type="button"
                    className="hover-underline"
                    onClick={() => setShowAllPayments((v) => !v)}
                    style={{ marginTop: '10px', alignSelf: 'flex-start', background: 'none', border: 0, padding: 0, cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--accent-text)' }}
                  >
                    {showAllPayments ? 'Show fewer' : `Show ${billing.payments.length - 8} more`}
                  </button>
                ) : null}
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', minWidth: 0 }}>
          {/* Websites */}
          <Card
            eyebrow={
              sites.length
                ? `${sites.length} site${sites.length === 1 ? '' : 's'} · ${runnableCount === sites.length ? (sites.length === 1 ? 'linked' : 'all linked') : `${runnableCount} linked`}`
                : 'Watched by the uptime monitor'
            }
            title="Websites"
          >
            <HostedSitesEditor
              account={account}
              allSites={allSites}
              onChanged={reloadSites}
              aside={(s) => {
                const a = perSite.find((p) => p.site.id === s.id)?.availability ?? null;
                return a === null ? null : (
                  <span className="t-num" title={`Uptime, last ${range} days`} style={{ fontSize: 'var(--text-xs)', color: dayColor(a) }}>
                    {pctText(a)}
                  </span>
                );
              }}
            />
          </Card>

          {/* Details */}
          <Card eyebrow="Account" title="Details">
            <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Detail label="Client">
                {account.client_id && clientName ? (
                  <Link to={`/clients/${account.client_id}`} className="hover-underline" style={{ color: 'var(--accent-text)' }}>
                    {clientName}
                  </Link>
                ) : (
                  <span style={{ color: 'var(--text-faint)' }}>Not attached</span>
                )}
              </Detail>
              <Detail label="Contact">
                {account.contact_name || account.contact_email ? (
                  <>
                    <div>{account.contact_name || '—'}</div>
                    {account.contact_email ? (
                      <a href={`mailto:${account.contact_email}`} className="hover-underline" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                        {account.contact_email}
                      </a>
                    ) : null}
                  </>
                ) : (
                  <span style={{ color: 'var(--text-faint)' }}>—</span>
                )}
              </Detail>
              <Detail label="Provider">{account.provider || '—'}</Detail>
              <Detail label="Plan">{account.plan || '—'}</Detail>
              <Detail label="Started">{account.started_on ? shortDate(account.started_on + 'T12:00:00', true) : '—'}</Detail>
              {billing?.subscription?.currentPeriodEnd ? (
                <Detail label={billing.subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'}>{shortDate(billing.subscription.currentPeriodEnd, true)}</Detail>
              ) : null}
              {notes ? (
                <Detail label="Notes">
                  <span style={{ whiteSpace: 'pre-wrap', color: 'var(--text-muted)' }}>{notes}</span>
                </Detail>
              ) : null}
            </dl>
          </Card>
        </div>
      </div>
    </Shell>
  );
}

function paymentHint(b: HostingBilling): string {
  if (!b.linked) return 'Link a Stripe customer to track it';
  const inv = b.openInvoices;
  if (inv?.overdueCount) return `${inv.overdueCount} overdue invoice${inv.overdueCount === 1 ? '' : 's'} · ${money(inv.amountDue)}`;
  if (b.paymentStatus === 'failing' && b.lastFailure) return `Failed ${shortDate(b.lastFailure.at)}${b.lastFailure.message ? ` · ${b.lastFailure.message}` : ''}`;
  if (b.subscription?.currentPeriodEnd) {
    return `${b.subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${shortDate(b.subscription.currentPeriodEnd)}`;
  }
  return b.subscription ? `Subscription ${b.subscription.status.replace(/_/g, ' ')}` : 'One-off payments only';
}

/** Stripe's charge descriptions are mostly plumbing ("Subscription update"). */
function describe(d: string | null): string {
  if (!d) return 'Payment';
  if (/^subscription (update|creation|cycle)$/i.test(d)) return 'Subscription';
  if (/^payment for invoice$/i.test(d)) return 'Invoice';
  return d;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen">
      <Sidebar />
      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">{children}</main>
    </div>
  );
}

function Stat({ label, value, hint, dotColor }: { label: string; value: React.ReactNode; hint?: React.ReactNode; dotColor?: string }) {
  return (
    <div
      className="ck-statcard hover-lift"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
        padding: 'var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        minWidth: 0,
      }}
    >
      <span className="t-eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
        {dotColor ? <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: dotColor, boxShadow: `0 0 0 3px color-mix(in srgb, ${dotColor} 20%, transparent)` }} /> : null}
        {label}
      </span>
      <span
        className="t-num"
        style={{
          // Shrinks on phones, where the cards sit two to a row.
          fontSize: 'clamp(1.25rem, 5vw, var(--text-2xl))',
          fontWeight: 600,
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
          color: 'var(--text-body)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </span>
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', minHeight: '1.3em', lineHeight: 'var(--leading-snug)' }} title={typeof hint === 'string' ? hint : undefined}>
        {hint}
      </span>
    </div>
  );
}

function Skeleton() {
  return <span style={{ display: 'inline-block', width: '96px', height: '0.9em', borderRadius: 'var(--radius-xs)', background: 'var(--surface-active)', verticalAlign: 'middle' }} />;
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr', gap: '12px', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
      <dt className="t-eyebrow" style={{ paddingTop: '2px' }}>
        {label}
      </dt>
      <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</dd>
    </div>
  );
}

function EmptyNote({ children, tone }: { children: React.ReactNode; tone?: 'down' }) {
  return (
    <div style={{ padding: 'var(--space-6) 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: tone === 'down' ? 'var(--status-down)' : 'var(--text-faint)' }}>
      {children}
    </div>
  );
}

function ChartTip({ active, payload }: TooltipContentProps<number, string>) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as { day: string; ms: number | null };
  return (
    <div style={{ padding: '8px 10px', background: 'var(--surface-raised)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-pop)' }}>
      <p className="t-eyebrow" style={{ margin: 0 }}>{shortDate(`${point.day}T12:00:00`, true)}</p>
      <p className="t-num" style={{ margin: '3px 0 0', fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--text-body)' }}>
        {point.ms === null ? 'No data' : `${point.ms}ms`}
      </p>
    </div>
  );
}
